/**
 * Package Details Page
 * Shows package information and available hotels with booking functionality
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { packageService, Package } from '../services/packageService';
import { hotelService, Hotel } from '../services/hotelService';
import { categoryService, Category } from '../services/categoryService';
import StartPlanningButton from '../components/Common/StartPlanningButton';
import { useTheme } from '../contexts/ThemeContext';
import {
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  StarIcon,
  CheckIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/Common/SEO';

const PackageDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { actualTheme } = useTheme();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState<Package | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [loading, setLoading] = useState(true);
  const [showBookingForm, setShowBookingForm] = useState(false);

  useEffect(() => {
    if (slug) {
      loadPackageData();
    }
  }, [slug]);

  const loadPackageData = async () => {
    try {
      setLoading(true);

      // Load package
      const packageData = await packageService.getPackageBySlug(slug!);
      if (!packageData) {
        navigate('/');
        return;
      }

      setPkg(packageData);

      // Load category
      if (packageData.categoryId) {
        const categoryData = await categoryService.getCategoryById(packageData.categoryId);
        setCategory(categoryData);
      }

      // Load hotels for this package
      const hotelsData = await hotelService.getHotelsByPackage(packageData.id);
      setHotels(hotelsData);

      // Auto-select first hotel if available
      if (hotelsData.length > 0) {
        setSelectedHotel(hotelsData[0]);
      }
    } catch (error) {
      console.error('Error loading package:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookNow = () => {
    if (selectedHotel) {
      // Navigate to booking page with package and hotel info
      navigate(`/book/package/${pkg?.id}`, {
        state: {
          package: pkg,
          hotel: selectedHotel
        }
      });
    } else {
      // Navigate to booking without hotel selection
      navigate(`/book/package/${pkg?.id}`, {
        state: {
          package: pkg
        }
      });
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-white'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800 mx-auto"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">Loading package...</p>
        </div>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className={`min-h-screen ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-white'} flex items-center justify-center`}>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-4">Package not found</h1>
          <Link to="/" className="text-primary-800 hover:text-primary-700">Return to homepage</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-white'}`}>
      <SEO
        title={pkg.name}
        description={pkg.description || `Explore ${pkg.name} package.`}
        image={pkg.images?.[0]}
        keywords={[...pkg.highlights || [], pkg.name, category?.name || '', 'safari', 'kenya']}
        structuredData={{
          "@type": "Product",
          "name": pkg.name,
          "description": pkg.description,
          "image": pkg.images,
          "offers": {
            "@type": "Offer",
            "price": pkg.price,
            "priceCurrency": pkg.currency || "KES",
            "availability": pkg.active ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
          },
          "aggregateRating": pkg.rating ? {
            "@type": "AggregateRating",
            "ratingValue": pkg.rating,
            "reviewCount": pkg.reviewCount || 1
          } : undefined
        }}
      />
      {/* Hero Image */}
      <div className="relative h-96 overflow-hidden">
        <img
          src={pkg.images?.[0] || 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2072&q=80'}
          alt={pkg.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

        {/* Back Button */}
        <Link
          to={category ? `/category/${category.id}` : '/'}
          className="absolute top-4 left-4 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-2 rounded-full transition-all"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </Link>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Package Header */}
            <div className="mb-8">
              {category && (
                <Link
                  to={`/category/${category.id}`}
                  className="inline-flex items-center mb-4 group"
                >
                  <span
                    className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold text-white shadow-lg backdrop-blur-md border-2 border-white/30 group-hover:scale-105 transition-all duration-300"
                    style={{
                      backgroundColor: `${category.color || '#1e3a8a'}DD`,
                      boxShadow: `0 8px 20px ${category.color || '#1e3a8a'}60, 0 0 15px ${category.color || '#1e3a8a'}40, inset 0 1px 0 rgba(255,255,255,0.2)`
                    }}
                  >
                    {category.name}
                  </span>
                </Link>
              )}
              <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">{pkg.name}</h1>
              {pkg.description && (
                <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                  {pkg.description}
                </p>
              )}
            </div>

            {/* Package Details */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 mb-8 shadow-lg">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-6">Package Details</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {pkg.duration && (
                  <div className="flex items-start">
                    <ClockIcon className="h-6 w-6 text-primary-800 dark:text-primary-400 mr-3 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-800 dark:text-white">Duration</h3>
                      <p className="text-slate-600 dark:text-slate-400">{pkg.duration}</p>
                    </div>
                  </div>
                )}

                {pkg.maxParticipants && (
                  <div className="flex items-start">
                    <UsersIcon className="h-6 w-6 text-primary-800 dark:text-primary-400 mr-3 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-800 dark:text-white">Group Size</h3>
                      <p className="text-slate-600 dark:text-slate-400">
                        {pkg.minParticipants || 1} - {pkg.maxParticipants} guests
                      </p>
                    </div>
                  </div>
                )}

                {pkg.difficulty && (
                  <div className="flex items-start">
                    <StarIcon className="h-6 w-6 text-primary-800 dark:text-primary-400 mr-3 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-800 dark:text-white">Difficulty</h3>
                      <p className="text-slate-600 dark:text-slate-400 capitalize">{pkg.difficulty}</p>
                    </div>
                  </div>
                )}

                {pkg.rating && pkg.rating > 0 && (
                  <div className="flex items-start">
                    <StarIcon className="h-6 w-6 text-yellow-500 mr-3 mt-1" />
                    <div>
                      <h3 className="font-semibold text-slate-800 dark:text-white">Rating</h3>
                      <p className="text-slate-600 dark:text-slate-400">
                        {pkg.rating.toFixed(1)} ({pkg.reviewCount || 0} reviews)
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Highlights */}
              {pkg.highlights && pkg.highlights.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-4">Highlights</h3>
                  <ul className="space-y-2">
                    {pkg.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600 dark:text-slate-400">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Includes/Excludes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                {pkg.includes && pkg.includes.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-slate-800 dark:text-white mb-3">Includes</h3>
                    <ul className="space-y-2">
                      {pkg.includes.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600 dark:text-slate-400 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {pkg.excludes && pkg.excludes.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-slate-800 dark:text-white mb-3">Excludes</h3>
                    <ul className="space-y-2">
                      {pkg.excludes.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-red-500 mr-2">×</span>
                          <span className="text-slate-600 dark:text-slate-400 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Hotels Section */}
            {hotels.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg">
                <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-6">
                  Available Hotels
                </h2>
                <p className="text-slate-600 dark:text-slate-400 mb-6">
                  Select a hotel for your stay. Bookings will be automatically sent to the hotel.
                </p>

                <div className="space-y-4">
                  {hotels.map((hotel) => (
                    <div
                      key={hotel.id}
                      onClick={() => setSelectedHotel(hotel)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedHotel?.id === hotel.id
                        ? 'border-primary-800 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-primary-400'
                        }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">
                            {hotel.name}
                          </h3>
                          {hotel.description && (
                            <p className="text-slate-600 dark:text-slate-400 text-sm mb-3">
                              {hotel.description}
                            </p>
                          )}

                          {/* Hotel Contacts */}
                          <div className="flex flex-wrap gap-4 mt-3">
                            {hotel.contactEmail && (
                              <a
                                href={`mailto:${hotel.contactEmail}`}
                                className="flex items-center text-sm text-primary-800 dark:text-primary-400 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <EnvelopeIcon className="h-4 w-4 mr-1" />
                                Email
                              </a>
                            )}
                            {hotel.contactPhone && (
                              <a
                                href={`tel:${hotel.contactPhone}`}
                                className="flex items-center text-sm text-primary-800 dark:text-primary-400 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <PhoneIcon className="h-4 w-4 mr-1" />
                                {hotel.contactPhone}
                              </a>
                            )}
                            {hotel.contactWhatsapp && (
                              <a
                                href={`https://wa.me/${hotel.contactWhatsapp.replace(/[^0-9]/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center text-sm text-green-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                WhatsApp
                              </a>
                            )}
                            {hotel.website && (
                              <a
                                href={hotel.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center text-sm text-primary-800 dark:text-primary-400 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <GlobeAltIcon className="h-4 w-4 mr-1" />
                                Website
                              </a>
                            )}
                          </div>
                        </div>

                        {selectedHotel?.id === hotel.id && (
                          <div className="ml-4">
                            <div className="w-6 h-6 rounded-full bg-primary-800 flex items-center justify-center">
                              <CheckIcon className="h-4 w-4 text-white" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Booking Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-xl">
              <div className="mb-6">
                {pkg.price ? (
                  <div>
                    <span className="text-3xl font-bold text-primary-800 dark:text-primary-400">
                      {pkg.currency || 'KES'} {pkg.price.toLocaleString()}
                    </span>
                    <span className="text-slate-600 dark:text-slate-400 ml-2">per person</span>
                  </div>
                ) : (
                  <div className="text-2xl font-bold text-slate-800 dark:text-white">
                    Price on request
                  </div>
                )}
              </div>

              {selectedHotel && (
                <div className="mb-6 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl">
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">Selected Hotel</p>
                  <p className="font-semibold text-slate-800 dark:text-white">{selectedHotel.name}</p>
                </div>
              )}

              {/* Start Planning + Book Now */}
              {/* Start Planning + Book Now side-by-side */}
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="w-full [&>div]:w-full [&>div>button#start-planning-btn]:w-full [&>div>button#start-planning-btn]:justify-center [&>div>button#start-planning-btn]:h-full">
                  <StartPlanningButton label="Plan" />
                </div>
                <button
                  onClick={handleBookNow}
                  className="w-full bg-primary-800 hover:bg-primary-700 text-white py-3.5 rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Book Now
                </button>
              </div>

              <p className="text-xs text-slate-500 dark:text-slate-400 mt-4 text-center">
                Booking will be automatically sent to the selected hotel
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageDetails;

